import { filter, find, get } from 'lodash';

import { EXPENSE_SUMMARIZED_ENTRY_MODE } from 'shared/constants/expenses';
import { expenseForecastFormHelpers } from 'components/underwriting/income-approach/expenses/expense-forecast/expenseForecastFormConfig';
import {
  INCOME_APPROACH_BASIS_TYPES,
  IncomeApproachBasisType,
  incomeApproachCalculations,
} from 'shared/calculations/incomeApproach';
import {
  BASIS_TYPES,
  DEFAULT_EXPENSES_KEYS,
  DEFAULT_EXPENSES_TITLES,
  EXPENSE_PERIOD_KEYS,
} from 'shared/constants/expenses';
import { formatPercentageString } from 'shared/utils/numberFormatters';
import { divide } from 'shared/utils/numberOperations';
import { Commercial, IncomeApproach, OtherIncomeItem, Residential } from 'types/underwriting/income-approach';
import { EntryMode, ExpenseHistory } from 'types/underwriting/income-approach/expenses';
import { UnderwritingOutput } from 'types/underwriting/underwriting';

export const getProFormaData = (underwritingData: UnderwritingOutput, basis: IncomeApproachBasisType) => {
  const { incomeApproach = {} as IncomeApproach, property } = underwritingData;
  const { grossBuildingArea, residentialUnits } = property;
  const {
    residential = {} as Residential,
    commercial = {} as Commercial,
    otherIncome = [] as OtherIncomeItem[],
  } = incomeApproach;

  const expenseHistoryEntryMode: EntryMode | undefined = get(
    underwritingData,
    'incomeApproach.expenses.expenseHistoryEntryMode'
  );
  const expenseForecastEntryMode: EntryMode | undefined = get(
    underwritingData,
    'incomeApproach.expenses.expenseForecastEntryMode'
  );

  const isSummarizedExpense = expenseHistoryEntryMode === EXPENSE_SUMMARIZED_ENTRY_MODE;
  const isSummarizedForecast = expenseForecastEntryMode === EXPENSE_SUMMARIZED_ENTRY_MODE;

  const isBorrower = basis === INCOME_APPROACH_BASIS_TYPES.borrower;
  const isProjected = basis === INCOME_APPROACH_BASIS_TYPES.projected;

  const isSummarizedBorrower = isSummarizedExpense && isBorrower;
  const isSummarizedProjected = isSummarizedForecast && isProjected;

  const { vcLossPercentage: residentialVCLossPercentage } = residential;
  const { reimbursements: commercialReimbursements, vcLossPercentage: commercialVCLossPercentage } = commercial;

  const residentialIncome = incomeApproachCalculations.calculateResidentialIncomeTotal(underwritingData, basis);
  const commercialIncome = incomeApproachCalculations.calculateCommercialIncomeTotal(underwritingData, basis);

  const potentialGrossIncome = incomeApproachCalculations.calculatePotentialGrossIncome(underwritingData, basis);

  const effectiveGrossIncome = incomeApproachCalculations.calculateEffectiveGrossIncome(underwritingData, basis);

  const expenseHistory = get(underwritingData, 'incomeApproach.expenses.expenseHistory', []);
  const expenseHistoryProForma = find(expenseHistory, (expense: ExpenseHistory) => {
    return expense.period === EXPENSE_PERIOD_KEYS.PROJECTION;
  });

  const borrowerExpenses = filter(
    expenseHistoryProForma?.expenses || [],
    (expense) => expense.category !== DEFAULT_EXPENSES_TITLES.realEstateTaxes
  );
  const borrowerTaxes = find(
    expenseHistoryProForma?.expenses || [],
    (expense) => expense.category === DEFAULT_EXPENSES_TITLES.realEstateTaxes
  );
  const borrowerTaxLiability = get(borrowerTaxes, 'value', 0);
  const expenseForecasts = get(underwritingData, 'incomeApproach.expenses.expenseForecasts', []);
  const expenseCategories = get(underwritingData, 'incomeApproach.expenses.expenseCategories', []);

  const expenseCategoriesToFilter = !isSummarizedExpense
    ? expenseCategories
    : filter(expenseCategories, (category) => Object.values(DEFAULT_EXPENSES_KEYS).includes(category.key));

  const filteredExpenseCategories = filter(
    expenseCategoriesToFilter,
    (category) => category.key !== DEFAULT_EXPENSES_KEYS.realEstateTaxes
  );

  const taxLiability =
    basis === INCOME_APPROACH_BASIS_TYPES.borrower
      ? borrowerTaxLiability
      : incomeApproachCalculations.calculateTaxLiability(underwritingData);

  const totalOperatingExpenses = incomeApproachCalculations.calculateTotalOperatingExpenses(underwritingData, basis);
  const totalOperatingExpensesExcludingTaxes =
    incomeApproachCalculations.calculateTotalOperatingExpensesExcludingRealEstateTaxes(underwritingData, basis);

  const netOperatingIncome = incomeApproachCalculations.calculateNetOperatingIncome(underwritingData, basis);

  const operatingExpenseRatio = incomeApproachCalculations.calculateOperatingExpenseRatio(underwritingData, basis);

  const data = [];

  if (residentialIncome > 0) {
    data.push({
      item: 'Potential Residential Income',
      value: residentialIncome,
      psf: divide(residentialIncome, grossBuildingArea),
      perUnit: divide(residentialIncome, residentialUnits),
    });
  }

  if (commercialIncome > 0) {
    data.push({
      item: 'Potential Commercial Income',
      value: commercialIncome,
      psf: divide(commercialIncome, grossBuildingArea),
      perUnit: divide(commercialIncome, residentialUnits),
    });
  }

  otherIncome?.forEach((item) => {
    const amount = item.amount || 0;

    data.push({
      item: item.name,
      value: amount,
      psf: divide(amount, grossBuildingArea),
      perUnit: divide(amount, residentialUnits),
    });
  });

  commercialReimbursements?.forEach((reimbursement) => {
    const amount = reimbursement.grossAnnualValue || 0;
    data.push({
      item: `Potential ${reimbursement.name} Reimbursement`,
      value: amount,
      psf: divide(amount, grossBuildingArea),
      perUnit: divide(amount, residentialUnits),
    });
  });

  data.push({
    item: 'Potential Gross Income',
    value: potentialGrossIncome,
    psf: divide(potentialGrossIncome, grossBuildingArea),
    perUnit: divide(potentialGrossIncome, residentialUnits),
  });

  if (residentialIncome > 0) {
    const residentialVCLoss = incomeApproachCalculations.calculateResidentialVCLoss(underwritingData, basis);
    data.push({
      item: `Less Residential V/C Loss @ ${formatPercentageString(residentialVCLossPercentage, 2)}`,
      value: -1 * residentialVCLoss,
      psf: divide(-1 * residentialVCLoss, grossBuildingArea),
      perUnit: divide(-1 * residentialVCLoss, residentialUnits),
    });
  }

  if (commercialIncome > 0) {
    const commercialVCLoss = incomeApproachCalculations.calculateCommercialVCLoss(underwritingData, basis);
    data.push({
      item: `Less Commercial V/C Loss @ ${formatPercentageString(commercialVCLossPercentage, 2)}`,
      value: -1 * commercialVCLoss,
      psf: divide(-1 * commercialVCLoss, grossBuildingArea),
      perUnit: divide(-1 * commercialVCLoss, residentialUnits),
    });
  }

  otherIncome?.forEach((item) => {
    const amount = item.amount || 0;
    const vcLossPercentage = item.vcLossPercentage || 0;
    const lossAmount = divide(amount * vcLossPercentage, 100);
    data.push({
      item: `Less ${item.name} @ ${formatPercentageString(vcLossPercentage, 2)}`,
      value: -1 * lossAmount,
      psf: divide(-1 * lossAmount, grossBuildingArea),
      perUnit: divide(-1 * lossAmount, residentialUnits),
    });
  });

  commercialReimbursements?.forEach((reimbursement) => {
    const amount = reimbursement.grossVCLoss || 0;
    data.push({
      item: `Less ${reimbursement.name} Reimbursement @ ${reimbursement.vcLoss} %`,
      value: -1 * amount,
      psf: divide(-1 * amount, grossBuildingArea),
      perUnit: divide(-1 * amount, residentialUnits),
    });
  });
  data.push({
    item: 'Effective Gross Income',
    value: effectiveGrossIncome,
    psf: divide(effectiveGrossIncome, grossBuildingArea),
    perUnit: divide(effectiveGrossIncome, residentialUnits),
  });
  data.push({
    item: 'Operating Expenses',
  });

  data.push({
    item: 'Real Estate Taxes',
    value: isSummarizedBorrower ? '-' : taxLiability,
    psf: isSummarizedBorrower ? '-' : divide(taxLiability, grossBuildingArea),
    perUnit: isSummarizedBorrower ? '-' : divide(taxLiability, residentialUnits),
  });

  filteredExpenseCategories?.forEach((expenseCategory) => {
    const { key, category } = expenseCategory;

    if (isSummarizedBorrower || isSummarizedProjected) {
      data.push({
        item: category,
        value: '-',
        psf: '-',
        perUnit: '-',
      });

      return;
    }

    const expenseForecast = find(expenseForecasts, (expenseForecast) => expenseForecast.key === key) || {};
    const { concludedValue = 0, basis: expenseForecastBasis = BASIS_TYPES.psf } = expenseForecast;
    const borrowerForecast = find(borrowerExpenses, (expense) => expense.category === category);
    const values = expenseForecastFormHelpers.getConcludedValueByBasis(
      concludedValue,
      expenseForecastBasis,
      grossBuildingArea || 0,
      residentialUnits || 0,
      effectiveGrossIncome
    );
    const borrowerValue = borrowerForecast?.value || 0;

    const value = basis === INCOME_APPROACH_BASIS_TYPES.borrower ? borrowerValue : values.total;
    data.push({
      item: category,
      value,
      psf: divide(value, grossBuildingArea),
      perUnit: divide(value, residentialUnits),
    });
  });

  data.push({
    item: 'Total Operating Expenses',
    value: totalOperatingExpenses,
    psf: divide(totalOperatingExpenses, grossBuildingArea),
    perUnit: divide(totalOperatingExpenses, residentialUnits),
  });

  data.push({
    item: 'Total Operating Expenses Excluding Real Estate Taxes',
    value: totalOperatingExpensesExcludingTaxes,
    psf: divide(totalOperatingExpensesExcludingTaxes, grossBuildingArea),
    perUnit: divide(totalOperatingExpensesExcludingTaxes, residentialUnits),
  });
  data.push({
    item: 'Net Operating Income',
    value: netOperatingIncome,
    psf: divide(netOperatingIncome, grossBuildingArea),
    perUnit: divide(netOperatingIncome, residentialUnits),
  });

  data.push({
    item: 'Operating Expense Ratio',
    value: formatPercentageString(operatingExpenseRatio, 2),
  });

  return data;
};
